import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  MenuItem,
  TextField,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTrainingStatusLabel, getTrainingStatusColor, TrainingStatus } from '../Enums/complianceEnums';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';
import { TrainingModuleDto, TrainingCategoryDto, trainingClient } from '../../../app/services/api/compliance/training';
import { UserContext } from '../../../components/shared/useUser';

const BUNDLE_CHIP_COLORS = ['primary', 'secondary', 'success', 'warning', 'info'] as const;

const getBundleChipColor = (name: string): typeof BUNDLE_CHIP_COLORS[number] => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return BUNDLE_CHIP_COLORS[Math.abs(hash) % BUNDLE_CHIP_COLORS.length];
};

const TrainingModulesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('training');
  const { selectedFranchise } = useContext(UserContext);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modules, setModules] = useState<TrainingModuleDto[]>([]);
  const [categories, setCategories] = useState<TrainingCategoryDto[]>([]);
  const [bundleNamesByModuleId, setBundleNamesByModuleId] = useState<Record<number, string[]>>({});
  const [filterCategory, setFilterCategory] = useState<number>(0);
  const [filterStatus, setFilterStatus] = useState<TrainingStatus | -1>(-1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<TrainingModuleDto | null>(null);
  const [cannotDeleteDialogOpen, setCannotDeleteDialogOpen] = useState(false);
  const [cannotDeleteReason, setCannotDeleteReason] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const rowsPerPage = 10;

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const franchiseId = selectedFranchise?.franchiseId || 0;
      const [modulesData, categoriesData, bundlesData] = await Promise.all([
        trainingClient.getModules({ franchiseId }),
        trainingClient.getCategories(),
        trainingClient.getBundles({ franchiseId }),
      ]);
      setModules(modulesData);
      setCategories(categoriesData);

      const bundleMap: Record<number, string[]> = {};
      bundlesData.forEach((bundle) => {
        bundle.modules.forEach((m) => {
          if (!bundleMap[m.moduleId]) bundleMap[m.moduleId] = [];
          bundleMap[m.moduleId].push(bundle.name);
        });
      });
      setBundleNamesByModuleId(bundleMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trainingModules.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (module: TrainingModuleDto) => {
    setModuleToDelete(module);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!moduleToDelete) return;
    
    try {
      setError(null);
      await trainingClient.deleteModule(moduleToDelete.id);
      setDeleteDialogOpen(false);
      setModuleToDelete(null);
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? 'Cannot delete training module that has assignments, completions, or certificates' : t('trainingModules.failedToDeleteModule');
      
      setDeleteDialogOpen(false);

      // If the API rejected because of existing assignments, show the specific warning modal
      const isAssignmentBlock = /assignment|assigned|in use|cannot delete|has active|trainees/i.test(message);
      if (isAssignmentBlock) {
        setCannotDeleteReason(message);
        setCannotDeleteDialogOpen(true);
        setModuleToDelete(null);
      } else {
        setError(message);
        setModuleToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setModuleToDelete(null);
  };

  const filteredModules = modules.filter(module => {
    if (filterCategory && module.categoryId !== filterCategory) return false;
    if (filterStatus >= 0 && module.status !== filterStatus) return false;
    return true;
  });

  const maxPages = () => {
    return Math.ceil(filteredModules.length / rowsPerPage);
  };

  const paginatedModules = filteredModules.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <PageTitle subTitle={t('trainingModules.subtitle')}>{t('trainingModules.title')}</PageTitle>
        <Button
          variant="primary"
          size="lg"
          startIcon={<SvgIcon><Plus /></SvgIcon>}
          onClick={() => navigate('/compliance/training/modules/builder')}
        >
          {t('trainingModules.newModule')}
        </Button>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
        >
          <TextField
            select
            label={t('trainingModules.category')}
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(Number(e.target.value));
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={0}>{t('trainingModules.allCategories')}</MenuItem>
            {categories.map((cat) => (
              <MenuItem
                key={cat.id}
                value={cat.id}
              >
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('trainingModules.status')}
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(Number(e.target.value));
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={-1}>{t('trainingModules.allStatuses')}</MenuItem>
            <MenuItem value={TrainingStatus.Draft}>{t('trainingModules.draft')}</MenuItem>
            <MenuItem value={TrainingStatus.Published}>{t('trainingModules.published')}</MenuItem>
            <MenuItem value={TrainingStatus.Archived}>{t('trainingModules.archived')}</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Section>
        {filteredModules.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('trainingModules.emptyText')}
            searchTerm={''}
            onClear={() => {
              setFilterCategory(0);
              setFilterStatus(-1); // -1 = no status filter (show all)
            }}
            onAdd={() => navigate('/compliance/training/modules/builder')}
            onAddIsCreating={false}
            addItemText={t('trainingModules.newModule')}
            onAddIsDisabled={false}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('trainingModules.title')}
                </Typography>
                <Pill text={`${filteredModules.length} ${filteredModules.length === 1 ? t('trainingModules.module') : t('trainingModules.modules')}`} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('trainingModules.name')}</TableCell>
                    <TableCell>{t('trainingModules.category')}</TableCell>
                    <TableCell>{t('trainingModules.coursePack')}</TableCell>
                    <TableCell>{t('trainingModules.duration')}</TableCell>
                    <TableCell>{t('trainingModules.version')}</TableCell>
                    <TableCell align="center">{t('trainingModules.status')}</TableCell>
                    <TableCell>{t('trainingModules.validity')}</TableCell>
                    <TableCell align="right">{t('trainingModules.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedModules.map((module) => (
                    <TableRow
                      key={module.id}
                      hover
                      onClick={() => navigate(`/compliance/training/modules/builder/${module.id}`)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.custom.gray[50],
                        },
                      }}
                    >
                      <TableCell>
                        <Tooltip
                          title={module.name}
                          placement="top"
                        >
                          <Typography
                            variant="textSM"
                            fontWeight={500}
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {module.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={module.categoryName}
                          placement="top"
                        >
                          <Typography
                            variant="textSM"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {module.categoryName}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(bundleNamesByModuleId[module.id]?.length ?? 0) === 0 ? (
                          <Typography variant="textSM"
                            color="text.secondary">
                            {t('trainingModules.notInCoursePack')}
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 0.5 }}>
                            {bundleNamesByModuleId[module.id].slice(0, 2).map((bundleName) => (
                              <Chip
                                key={bundleName}
                                label={bundleName}
                                size="small"
                                color={getBundleChipColor(bundleName)}
                                variant="outlined"
                                sx={{ maxWidth: 140 }}
                              />
                            ))}
                            {bundleNamesByModuleId[module.id].length > 2 && (
                              <Tooltip title={bundleNamesByModuleId[module.id].slice(2).join(', ')}
                                arrow>
                                <Chip
                                  label={`+${bundleNamesByModuleId[module.id].length - 2}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          {module.estimatedDurationMinutes} {t('trainingModules.minutes')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          v{module.version}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getTrainingStatusLabel(module.status)}
                          color={getTrainingStatusColor(module.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          {module.validityPeriodDays ? `${module.validityPeriodDays} ${t('trainingModules.days')}` : t('trainingModules.lifetime')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {module.status !== TrainingStatus.Published && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/compliance/training/modules/builder/${module.id}`);
                            }}
                            title={t('trainingModules.edit')}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(module);
                          }}
                          color="error"
                          title={t('trainingModules.delete')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        nameOfObject={moduleToDelete?.name!}
      />

      {/* Cannot Delete Warning Dialog – shown when module has active assignments */}
      <Dialog
        open={cannotDeleteDialogOpen}
        onClose={() => setCannotDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          {t('trainingModules.cannotDeleteTitle', 'Cannot Delete Module')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning"
            sx={{ mb: 2 }}>
            {t(
              'trainingModules.cannotDeleteHasAssignments',
              'This module cannot be deleted because it has one or more active training assignments. Please remove or reassign all trainees before deleting this module.',
            )}
          </Alert>
          {cannotDeleteReason && (
            <Typography 
              sx={{ color: 'text.secondary', mt: 1 }}>
              {t('trainingModules.apiError', 'Details')}: {cannotDeleteReason}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="primary"
            size="lg"
            onClick={() => setCannotDeleteDialogOpen(false)}
          >
            {t('common.ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TrainingModulesPage;